/**
 * `<SolvesList/>` — the team's **Solves** (#1959, slice 1E of #1950).
 *
 * The founder's model: every date is one fact stored once; nobody sees,
 * names or creates "periods"; a solve is a date range chosen at solve time
 * with no identity afterwards. This list is the lead's read-only history of
 * those ranges: one row per solve, newest first — the dates, the state
 * (`Published` / `Solved · not published` / `Not solved` / `Solving`), who
 * ran it, when — and *Open*, which opens the roster page at those dates
 * (`/teams/:id/roster?from&to`).
 *
 * Deliberately no create, rename or delete: dates are only ever solved from
 * the roster (its solve console, *Solve these dates* on empty dates) or by
 * dragging across the date line on the Solves page. Internally each row is
 * one stored window (`GET /api/teams/:id/cycles`, archived ones hidden);
 * `summary.last_run_*` (the newest solve job) supplies who and when.
 *
 * Focal component: the list itself (feedback_ui_section_focal_component).
 */
import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

import { apiFetch } from "../../api/client";
import { Badge, type BadgeProps, ErrorState, Skeleton } from "../../components/ui";
import { useAuth } from "../../lib/AuthContext";
import { cn } from "../../lib/cn";
import {
  capWindow,
  cycleWindow,
  formatWindowSpan,
  searchWithWindow,
} from "../../lib/rosterDatesWindow";
import { useCascadeOnce } from "../../lib/useCascadeOnce";
import { useSkeletonCrossFade } from "../../lib/useSkeletonCrossFade";
import type { Cycle } from "./types";

export type SolveRowState = "published" | "solved" | "not_solved" | "solving";

export const SOLVE_STATE_LABEL: Record<SolveRowState, string> = {
  published: "Published",
  solved: "Solved · not published",
  not_solved: "Not solved",
  solving: "Solving",
};

const SOLVE_STATE_TONE: Record<SolveRowState, NonNullable<BadgeProps["tone"]>> = {
  published: "success",
  solved: "accent",
  not_solved: "neutral",
  solving: "warning",
};

type SolveSource = Pick<Cycle, "state" | "roster_held_as_drafts" | "summary">;

/** One window's state as the Solves list says it. */
export function solveRowState(c: SolveSource): SolveRowState {
  const run = c.summary?.last_run_status;
  if (c.state === "solving" || run === "queued" || run === "running") return "solving";
  if (c.state === "published") return "published";
  const status = c.summary?.solve_status;
  if (c.roster_held_as_drafts || (status && status !== "not_solved" && status !== "INFEASIBLE")) {
    return "solved";
  }
  return "not_solved";
}

/** The roster page at a window's dates (cut to the page's 62-day limit). */
export function rosterHrefForSolve(
  teamId: string,
  c: Pick<Cycle, "horizon_start" | "horizon_days">,
): string {
  return `/teams/${teamId}/roster${searchWithWindow("", capWindow(cycleWindow(c)).window)}`;
}

const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/** "2 Oct, 14:05" in local time (the year only when it is not this year). */
export function formatSolvedAt(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const sameYear = d.getFullYear() === now.getFullYear();
  // Fixed three-letter months: some ICU builds spell September "Sept".
  const month = MONTH_SHORT[d.getMonth()] ?? "";
  const day = `${d.getDate()} ${month}${sameYear ? "" : ` ${d.getFullYear()}`}`;
  const time = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  return `${day}, ${time}`;
}

function sortKey(c: Pick<Cycle, "created_at" | "summary">): string {
  return c.summary?.last_run_at ?? c.created_at;
}

/** Newest first: by the last run, else by when the window was made. */
export function sortSolves<C extends Pick<Cycle, "created_at" | "summary">>(
  rows: readonly C[],
): C[] {
  return [...rows].sort((a, b) => sortKey(b).localeCompare(sortKey(a)));
}

export function SolvesList({
  teamId,
  limit,
  allHref,
}: {
  teamId: string;
  /** Show only the newest `limit` rows (the team page); all when absent. */
  limit?: number;
  /** Where *All solves* goes when rows are hidden by `limit`. */
  allHref?: string;
}) {
  const { accountId } = useAuth();
  const query = useQuery({
    queryKey: ["team", teamId, "cycles", accountId],
    enabled: Boolean(teamId && accountId),
    queryFn: () => apiFetch<Cycle[]>(`/api/teams/${teamId}/cycles`),
  });
  // #2172 — the skeleton cross-fades into the list, whose rows cascade in
  // the first time they show.
  const fade = useSkeletonCrossFade(query.isLoading);
  const cascade = useCascadeOnce((query.data?.length ?? 0) > 0);

  if (query.isLoading) {
    return (
      <div ref={fade.ref} className="flex flex-col gap-2" data-testid="solves-loading">
        <Skeleton className="h-10" />
        <Skeleton className="h-10" />
      </div>
    );
  }
  if (query.isError) {
    return <ErrorState title="Couldn't load the solves" onRetry={() => query.refetch()} />;
  }

  const all = sortSolves((query.data ?? []).filter((c) => c.phase !== "archived"));
  if (all.length === 0) {
    return (
      <p className="text-sm text-fg-muted" data-testid="solves-empty">
        Nothing solved yet.{" "}
        <Link to={`/teams/${teamId}/roster`} className="font-medium text-accent-fg underline">
          Open the roster
        </Link>{" "}
        and solve a range of dates.
      </p>
    );
  }
  const rows = limit != null ? all.slice(0, limit) : all;
  const hidden = all.length - rows.length;

  return (
    <div ref={fade.ref} className={cn("flex flex-col gap-2", fade.contentClassName)}>
      <ul
        className="flex flex-col divide-y divide-border"
        data-testid="solves-list"
        data-cascade={cascade}
      >
        {rows.map((c) => {
          const state = solveRowState(c);
          const href = rosterHrefForSolve(teamId, c);
          const dates = formatWindowSpan(cycleWindow(c));
          const by = c.summary?.last_run_by ?? null;
          const at = c.summary?.last_run_at ? formatSolvedAt(c.summary.last_run_at) : "";
          return (
            <li
              key={c.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2"
              data-testid="solves-row"
            >
              <Link
                to={href}
                className="min-w-[8rem] font-medium text-fg tabular-nums hover:underline"
                data-testid="solves-row-dates"
              >
                {dates}
              </Link>
              <Badge tone={SOLVE_STATE_TONE[state]} data-testid="solves-row-state">
                {SOLVE_STATE_LABEL[state]}
              </Badge>
              <span className="min-w-0 flex-1 text-xs text-fg-muted" data-testid="solves-row-who">
                {by || at ? [by, at].filter(Boolean).join(" · ") : "—"}
              </span>
              <Link
                to={href}
                className="inline-flex items-center gap-1 text-xs font-medium text-accent-fg hover:underline"
                aria-label={`Open the roster at ${dates}`}
                data-testid="solves-row-open"
              >
                Open
                <ArrowRight className="h-3 w-3" aria-hidden="true" />
              </Link>
            </li>
          );
        })}
      </ul>
      {hidden > 0 && allHref ? (
        <Link
          to={allHref}
          className="self-start text-xs font-medium text-accent-fg hover:underline"
          data-testid="solves-all"
        >
          All solves ({all.length})
        </Link>
      ) : null}
    </div>
  );
}
